import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, SealCheck } from "@phosphor-icons/react";
import { lineParent, MaskLine } from "./motion";
import { useI18n } from "@/i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.9]);
  const lines = t.hero.titleLines;

  return (
    <section id="inicio" ref={ref} className="relative min-h-[100svh] overflow-hidden flex flex-col justify-end">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Wholesale distribution warehouse" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-[#050505]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-[#050505]/70" />
      <div className="absolute inset-0 z-10 grid-lines opacity-60" />

      <div className="relative z-20 w-full max-w-screen-2xl mx-auto px-4 md:px-8 pb-14 md:pb-20 pt-32">
        <motion.div variants={lineParent} initial="hidden" animate="show">
          <MaskLine className="font-mono text-[11px] md:text-xs uppercase tracking-[0.35em] text-[#ff4500] mb-6">
            <span className="inline-flex items-center gap-2">
              <span className="w-2 h-2 bg-[#ff4500] animate-pulse" />
              {t.hero.badge}
            </span>
          </MaskLine>

          <h1 className="font-clash font-black uppercase leading-[0.9] tracking-tight text-[#f5f5f5] text-[13vw] md:text-[9vw] lg:text-[8rem]">
            <MaskLine>{lines[0]}</MaskLine>
            <MaskLine>
              <span className="text-outline">{lines[1]}</span>
            </MaskLine>
            <MaskLine>
              <span className="text-[#ff4500]">{lines[2]}</span>
            </MaskLine>
          </h1>

          <div className="mt-8 grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-6">
              <MaskLine className="font-plex text-base md:text-lg text-[#a3a3a3] max-w-xl">
                {t.hero.desc}
              </MaskLine>
            </div>

            <div className="md:col-span-6 flex flex-col sm:flex-row gap-3 md:justify-end">
              <a
                href="#registro"
                data-testid="hero-cta-primary"
                className="bg-[#ff4500] hover:bg-[#e03c00] text-[#050505] font-semibold px-8 py-4 text-center transition-colors"
              >
                {t.hero.ctaPrimary}
              </a>
              <a
                href="#ventajas"
                data-testid="hero-cta-secondary"
                className="border border-white/20 hover:border-[#ff4500] text-[#f5f5f5] font-semibold px-8 py-4 text-center transition-colors"
              >
                {t.hero.ctaSecondary}
              </a>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-12 pt-8 border-t border-white/10 grid grid-cols-3 gap-4 max-w-3xl"
        >
          {t.hero.metrics.map((m) => (
            <div key={m.l}>
              <p className="font-clash font-bold text-3xl md:text-5xl text-[#f5f5f5]">{m.v}</p>
              <p className="font-mono text-[10px] md:text-xs uppercase tracking-wider text-[#a3a3a3] mt-1">{m.l}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="absolute right-6 bottom-8 z-20 hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#a3a3a3]">
        <SealCheck size={16} className="text-emerald-400" /> {t.hero.verified}
        <ArrowDown size={16} className="ml-3 animate-bounce text-[#ff4500]" />
      </div>
    </section>
  );
}
